type ErrorRecord = Partial<Record<string, string>>;

type FocusFirstInvalidFieldParams = {
  errors: ErrorRecord;
  selectorByErrorKey: Partial<Record<string, string>>;
  // Scopes lookups to a form's subtree so a duplicate id/selector from
  // another mounted form (e.g. a background screen behind a secondary
  // drawer) can't be matched instead.
  root?: ParentNode;
};

const FOCUSABLE_DESCENDANT_SELECTOR =
  'input, select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])';

const hasScrollIntoView = (
  element: HTMLElement,
): element is HTMLElement & {
  scrollIntoView: (options?: ScrollIntoViewOptions) => void;
} => {
  return typeof element.scrollIntoView === 'function';
};

const safeScrollIntoView = (
  element: HTMLElement,
  options: ScrollIntoViewOptions,
) => {
  if (hasScrollIntoView(element)) {
    element.scrollIntoView(options);
  }
};

const safeFocus = (
  element: HTMLElement,
  options?: FocusOptions,
) => {
  try {
    element.focus(options);
  } catch {
    element.focus();
  }
};

const isVisible = (element: HTMLElement) => {
  if (!element.isConnected) {
    return false;
  }

  let currentElement: HTMLElement | null = element;
  while (currentElement) {
    const { display, visibility } =
      window.getComputedStyle(currentElement);

    if (
      display === 'none' ||
      visibility === 'hidden' ||
      visibility === 'collapse'
    ) {
      return false;
    }

    currentElement = currentElement.parentElement;
  }

  return true;
};

const isDisabled = (element: HTMLElement) => {
  if ('disabled' in element) {
    return Boolean(
      (
        element as
          | HTMLInputElement
          | HTMLSelectElement
          | HTMLTextAreaElement
          | HTMLButtonElement
      ).disabled,
    );
  }

  return element.getAttribute('aria-disabled') === 'true';
};

const isFocusable = (element: HTMLElement) => {
  if (element.tabIndex >= 0) {
    return true;
  }

  return ['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON', 'A'].includes(
    element.tagName,
  );
};

const resolveFocusableTarget = (
  selector: string,
  root: ParentNode,
) => {
  const matchingElements = root.querySelectorAll(selector);

  for (const element of matchingElements) {
    if (!(element instanceof HTMLElement)) {
      continue;
    }

    if (isDisabled(element) || !isVisible(element)) {
      continue;
    }

    if (isFocusable(element)) {
      return element;
    }

    const nestedFocusable = element.querySelector<HTMLElement>(
      FOCUSABLE_DESCENDANT_SELECTOR,
    );

    if (
      nestedFocusable &&
      !isDisabled(nestedFocusable) &&
      isVisible(nestedFocusable)
    ) {
      return nestedFocusable;
    }
  }

  return null;
};

export const isElementBefore = (
  element: HTMLElement,
  reference: HTMLElement,
) => {
  return Boolean(
    // eslint-disable-next-line no-bitwise
    element.compareDocumentPosition(reference) &
    Node.DOCUMENT_POSITION_FOLLOWING,
  );
};

export const focusElement = (element: HTMLElement) => {
  safeScrollIntoView(element, {
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  });
  safeFocus(element, { preventScroll: true });
};

export const findFirstInvalidField = ({
  errors,
  selectorByErrorKey,
  root = document,
}: FocusFirstInvalidFieldParams) => {
  const invalidTargets = Object.keys(selectorByErrorKey)
    .filter((key) => Boolean(errors[key]))
    .map((key) =>
      resolveFocusableTarget(
        selectorByErrorKey[key] as string,
        root,
      ),
    )
    .filter((target): target is HTMLElement => Boolean(target));

  return invalidTargets.reduce<HTMLElement | null>(
    (firstTarget, target) =>
      !firstTarget || isElementBefore(target, firstTarget)
        ? target
        : firstTarget,
    null,
  );
};

export const focusFirstInvalidField = ({
  errors,
  selectorByErrorKey,
  root,
}: FocusFirstInvalidFieldParams) => {
  const target = findFirstInvalidField({
    errors,
    selectorByErrorKey,
    root,
  });

  if (!target) {
    return false;
  }

  focusElement(target);
  return true;
};

export const focusFirstInvalidFieldWithRetry = ({
  errors,
  selectorByErrorKey,
  root,
}: FocusFirstInvalidFieldParams) => {
  // Defer to the next frame so this runs after React commits any setState
  // calls (e.g. newly-shown error text) made earlier in the same
  // synchronous submit handler. Querying synchronously can target a
  // pre-shift layout and require a second click to land correctly.
  window.requestAnimationFrame(() => {
    const didFocus = focusFirstInvalidField({
      errors,
      selectorByErrorKey,
      root,
    });

    if (!didFocus) {
      window.requestAnimationFrame(() => {
        focusFirstInvalidField({
          errors,
          selectorByErrorKey,
          root,
        });
      });
    }
  });
};
